import type { Comparison, Condition, VariableValue } from '@sw/schema'

export type Role = 'listener' | 'broadcaster' | 'operator' | 'editor'

/**
 * Compares a world variable against an expected value.
 *
 * An unset variable is never equal to anything and never orders against
 * anything, so a rule waiting on `clues >= 3` stays quiet until something has
 * actually counted a clue. Ordering comparisons need two numbers; asking
 * whether a string is greater than a number is a mistake, and quietly saying
 * "false" is more useful than throwing at showtime.
 */
export function compareValues(
  actual: VariableValue | undefined,
  comparison: Comparison,
  expected: VariableValue,
): boolean {
  if (comparison === 'eq') return actual === expected
  if (comparison === 'neq') return actual !== expected

  if (typeof actual !== 'number' || typeof expected !== 'number') return false

  switch (comparison) {
    case 'lt':
      return actual < expected
    case 'lte':
      return actual <= expected
    case 'gt':
      return actual > expected
    case 'gte':
      return actual >= expected
  }
}

export interface ConditionContext {
  variables: Readonly<Record<string, VariableValue>>
  /** Minutes since local midnight at the placement. */
  localMinuteOfDay: number | undefined
  roles: readonly Role[]
}

function withinTimeWindow(startMinute: number, endMinute: number, minute: number): boolean {
  // A window that ends before it starts wraps past midnight, which is exactly
  // the case a night-time world cares about.
  return startMinute <= endMinute
    ? minute >= startMinute && minute <= endMinute
    : minute >= startMinute || minute <= endMinute
}

export function evaluateCondition(condition: Condition, ctx: ConditionContext): boolean {
  switch (condition.type) {
    case 'variable':
      return compareValues(ctx.variables[condition.name], condition.comparison, condition.value)
    case 'timeWindow':
      return ctx.localMinuteOfDay === undefined
        ? false
        : withinTimeWindow(condition.startMinute, condition.endMinute, ctx.localMinuteOfDay)
    case 'role':
      return ctx.roles.includes(condition.role)
  }
}

export function evaluateConditions(
  conditions: readonly Condition[],
  ctx: ConditionContext,
): boolean {
  return conditions.every((condition) => evaluateCondition(condition, ctx))
}
